import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../../App';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { 
  ArrowLeft, Building2, Search, Eye, EyeOff, Calendar, Heart, Landmark,
  ChevronRight, Users, Filter, X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

/**
 * INSTITUTION DISCOVERY PAGE (BUILD 1 + BUILD 6)
 * 
 * Visibility Rules:
 * - Public Institutions: Appear in Discover → Institutions, Browsable, NO student exposure
 * - Private Institutions: Invitation only, Not searchable
 */

const TYPE_CONFIG = {
  school: { icon: Building2, color: 'var(--lk-earth-brown)', label: 'School' },
  program: { icon: Calendar, color: 'var(--lk-secondary-orange)', label: 'Educational Program' },
  nonprofit: { icon: Heart, color: 'var(--lk-primary-green)', label: 'Nonprofit' },
  institution: { icon: Landmark, color: 'var(--lk-soft-stone)', label: 'Institution' }
};

export default function InstitutionDiscover() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useContext(AuthContext);
  
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState(searchParams.get('type') || 'all');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadInstitutions();
  }, [selectedType]);

  const loadInstitutions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = new URLSearchParams();
      if (selectedType !== 'all') params.append('type', selectedType);
      if (searchQuery.trim()) params.append('q', searchQuery.trim());
      
      const response = await axios.get(`${API}/api/institutions/discover?${params.toString()}`, { headers });
      setInstitutions(response.data.institutions || []);
    } catch (error) {
      console.error('Failed to load institutions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadInstitutions();
  };

  const filteredInstitutions = institutions.filter(inst => 
    !searchQuery || 
    inst.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    inst.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-50" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center h-14">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-black/5">
              <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
            </button>
            <h1 className="flex-1 text-center font-semibold" style={{ color: 'var(--fy-text-primary)' }}>
              Discover Institutions
            </h1>
            <button 
              onClick={() => setShowFilters(!showFilters)} 
              className="p-2 -mr-2 rounded-full hover:bg-black/5"
            >
              <Filter className="w-5 h-5" style={{ color: showFilters ? 'var(--lk-primary-green)' : 'var(--fy-text-secondary)' }} />
            </button>
          </div>
        </div>
      </header>

      {/* Search & Filters */}
      <div className="px-4 py-4 space-y-3" style={{ borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <form onSubmit={handleSearch} className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: 'var(--fy-text-disabled)' }} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search institutions..."
            className="w-full pl-10 pr-4 py-3 rounded-xl text-sm"
            style={{ 
              backgroundColor: 'var(--fy-surface-muted)', 
              border: '1px solid var(--fy-border-subtle)',
              color: 'var(--fy-text-primary)'
            }}
            data-testid="institution-search-input"
          />
          {searchQuery && (
            <button 
              type="button"
              onClick={() => { setSearchQuery(''); loadInstitutions(); }}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <X className="w-4 h-4" style={{ color: 'var(--fy-text-disabled)' }} />
            </button>
          )}
        </form>

        {showFilters && (
          <div className="flex gap-2 overflow-x-auto pb-1">
            <button
              onClick={() => setSelectedType('all')}
              className="px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap"
              style={{ 
                backgroundColor: selectedType === 'all' ? 'var(--lk-earth-brown)' : 'var(--fy-surface-muted)',
                color: selectedType === 'all' ? 'var(--lk-warm-sand)' : 'var(--fy-text-secondary)'
              }}
            >
              All
            </button>
            {Object.entries(TYPE_CONFIG).map(([key, config]) => (
              <button
                key={key}
                onClick={() => setSelectedType(key)}
                className="px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap flex items-center gap-1"
                style={{ 
                  backgroundColor: selectedType === key ? config.color : 'var(--fy-surface-muted)',
                  color: selectedType === key ? 'var(--lk-warm-sand)' : 'var(--fy-text-secondary)'
                }}
              >
                {config.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Create CTA */}
      <div className="px-4 py-4">
        <Card 
          className="p-4 flex items-center gap-3 cursor-pointer hover:shadow-sm"
          style={{ backgroundColor: 'var(--lk-sage-muted)', border: '1px solid var(--lk-primary-green)' }}
          onClick={() => navigate('/membership')}
          data-testid="start-institution-cta"
        >
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--lk-primary-green)' }}>
            <Building2 className="w-5 h-5" style={{ color: 'var(--lk-warm-sand)' }} />
          </div>
          <div className="flex-1">
            <p className="font-medium" style={{ color: 'var(--lk-primary-green)' }}>Start a School or Organization</p>
            <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>Entry begins at account creation</p>
          </div>
          <ChevronRight className="w-5 h-5" style={{ color: 'var(--lk-primary-green)' }} />
        </Card>
      </div>

      {/* Results */}
      <main className="px-4 pb-20">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" style={{ borderColor: 'var(--lk-earth-brown)' }} />
          </div>
        ) : filteredInstitutions.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--fy-text-disabled)' }} />
            <p className="font-medium mb-1" style={{ color: 'var(--fy-text-secondary)' }}>
              {searchQuery ? 'No institutions found' : 'No public institutions yet'}
            </p>
            <p className="text-sm mb-4" style={{ color: 'var(--fy-text-disabled)' }}>
              {searchQuery ? 'Try a different search term' : 'Schools and organizations join through the entry flow'}
            </p>
            <Button 
              onClick={() => navigate('/membership')}
              className="rounded-full"
              style={{ backgroundColor: 'var(--lk-earth-brown)', color: 'var(--lk-warm-sand)' }}
            >
              Create an Account
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
              {filteredInstitutions.length} public institution{filteredInstitutions.length !== 1 ? 's' : ''}
            </p>
            {filteredInstitutions.map((institution, idx) => {
              const typeConfig = TYPE_CONFIG[institution.type] || TYPE_CONFIG.institution;
              const TypeIcon = typeConfig.icon;
              return (
                <Card 
                  key={institution.institution_id || idx}
                  className="p-4 cursor-pointer hover:shadow-md transition-shadow"
                  style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
                  onClick={() => navigate(`/institutions/${institution.institution_id}`)}
                  data-testid={`institution-card-${institution.institution_id}`}
                >
                  <div className="flex items-start gap-3">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden"
                      style={{ backgroundColor: `${typeConfig.color}20` }}
                    >
                      {institution.logo_url ? (
                        <img src={institution.logo_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <TypeIcon className="w-6 h-6" style={{ color: typeConfig.color }} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold truncate" style={{ color: 'var(--fy-text-primary)' }}>
                          {institution.name}
                        </h3>
                        <span 
                          className="text-xs px-2 py-0.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: `${typeConfig.color}20`, color: typeConfig.color }}
                        >
                          {typeConfig.label}
                        </span>
                      </div>
                      {institution.description && (
                        <p className="text-sm line-clamp-2 mb-2" style={{ color: 'var(--fy-text-secondary)' }}>
                          {institution.description}
                        </p>
                      )}
                      <div className="flex items-center gap-3 text-xs" style={{ color: 'var(--fy-text-disabled)' }}>
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {institution.member_count || 0} members
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          Public
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--fy-text-disabled)' }} />
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
